
import { Book } from "@/types/book";
import { Bundle } from "@/types/bundle";
import { cn } from "@/lib/utils";
import { Sparkles, Clock } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useEffect, useRef, useState } from "react";
import { EnhancedBookCover } from "@/components/ui/enhanced-book-cover";

interface BundleBookGridProps {
  books: Book[];
  bundle?: Bundle | null;
  isEarlyAccess?: boolean;
  selectedEarlyAccessBook?: number | null;
}

export function BundleBookGrid({
  books,
  bundle,
  isEarlyAccess = false,
  selectedEarlyAccessBook
}: BundleBookGridProps) {
  const isMobile = useIsMobile();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Auto-scroll effect with fade transition
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || books.length === 0) return;

    let scrollPosition = 0;
    const scrollSpeed = 0.5; // pixels per frame
    const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;

    const scroll = () => {
      if (scrollContainer && !isTransitioning) {
        scrollPosition += scrollSpeed;
        
        // When reaching the end, trigger fade transition
        if (scrollPosition >= maxScroll) {
          setIsTransitioning(true);
          
          // Fade out, reset position, then fade in
          setTimeout(() => {
            scrollPosition = 0;
            scrollContainer.scrollLeft = scrollPosition;
            setIsTransitioning(false);
          }, 500); // Half second fade duration
        } else {
          scrollContainer.scrollLeft = scrollPosition;
        }
      }
    };

    const intervalId = setInterval(scroll, 16); // ~60fps

    return () => clearInterval(intervalId);
  }, [books.length, isTransitioning]);
  
  return (
    <div 
      style={{
        boxShadow: '0 0 30px rgba(147, 51, 234, 0.8), inset 0 0 30px rgba(147, 51, 234, 0.1)'
      }} 
      className="p-6 border-2 border-purple-500 shadow-[0_0_20px_rgba(147,51,234,0.6)] bg-slate-950/80 backdrop-blur-md rounded-xl"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-display font-semibold text-white">
          {isEarlyAccess ? 'Pre-Order Bundle' : 'Bundle Contents'}
        </h3>
        <div className="flex items-center gap-1 text-blue-400 text-sm">
          <span>{books.length} books</span>
        </div>
      </div>

      {isEarlyAccess && (
        <div className="bg-purple-500/20 border-purple-500/30 p-3 mb-4 rounded-lg backdrop-blur-sm">
          <div className="flex items-center gap-2 text-purple-400 mb-1">
            <Sparkles className="w-4 h-4" />
            <span className="font-display font-medium">Early Access Benefit</span>
          </div>
          <p className="text-white/80 text-sm">
            After purchase, you'll be able to choose one book to unlock immediately. The rest will be available when the bundle launches.
          </p>
        </div>
      )}

      <div className="w-full overflow-hidden">
        <div 
          ref={scrollRef}
          className={cn(
            "flex space-x-3 pb-4 overflow-x-hidden transition-opacity duration-500",
            isTransitioning ? "opacity-20" : "opacity-100"
          )}
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {books.map(book => (
            <div 
              key={book.id} 
              className={cn(
                "relative transition-all duration-200 w-[120px] md:w-[160px] flex-shrink-0",
                selectedEarlyAccessBook === book.id && isEarlyAccess ? "ring-2 ring-purple-400 scale-105" : ""
              )}
            >
              <div className="aspect-[3/4] rounded-lg overflow-hidden bg-white/10">
                <EnhancedBookCover 
                  src={book.cover_image_url || ''} 
                  alt={book.title} 
                  className="w-full h-full" 
                  bookId={book.id}
                />

                {/* Early Access Indicator */}
                {selectedEarlyAccessBook === book.id && isEarlyAccess && (
                  <div className="absolute inset-0 bg-purple-500/20 flex items-center justify-center">
                    <div className="bg-purple-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      Early Access
                    </div>
                  </div>
                )}
                
                {/* Available Soon for early access */}
                {selectedEarlyAccessBook !== book.id && isEarlyAccess && (
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <div className="bg-black/70 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Available Soon
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mt-2">
                <h4 className="text-white text-sm font-medium line-clamp-2 transition-colors">
                  {book.title}
                </h4>
              </div>
            </div>
          ))}
        </div>
      </div>

      {bundle?.description && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <p className="text-white/70 text-sm">{bundle.description}</p>
        </div>
      )}
    </div>
  );
}
